import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar        from '../components/Navbar';
import Toast         from '../components/Toast';
import StatCard      from '../components/StatCard';
import ColisCard     from '../components/ColisCard';
import FormDepot     from '../components/FormDepot';
import ModalAffecter from '../components/ModalAffecter';
import { useApp }    from '../data/ColisContext';
import { ZONES }     from '../data/constants';
import { formatFCFA } from '../utils/helpers';

const TABS_USER = [
  { key: 'depot',     label: 'Nouveau dépôt',   icon: '📥' },
  { key: 'tous',      label: 'Tous les colis',   icon: '📋' },
  { key: 'en_cours',  label: 'En cours',         icon: '🚚' },
  { key: 'livres',    label: 'Livrés',           icon: '✅' },
  { key: 'echecs',    label: 'Échecs',           icon: '❌' },
];

const TABS_ADMIN = [
  { key: 'tous',      label: 'Tous les colis',   icon: '📋' },
  { key: 'en_cours',  label: 'En cours',         icon: '🚚' },
  { key: 'livres',    label: 'Livrés',           icon: '✅' },
  { key: 'echecs',    label: 'Échecs',           icon: '❌' },
];

export default function DashboardPage() {
  const { user, colis, stats, adminColis, adminStats } = useApp();
  const [searchParams] = useSearchParams();
  const [tab,        setTab]        = useState('tous');
  const [filterZone, setFilterZone] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [modalId,    setModalId]    = useState(null);

  const isAdmin = !!user?.is_admin;
  const list = isAdmin ? adminColis : colis;
  const effectiveStats = isAdmin ? adminStats : stats;
  const tabs = isAdmin ? TABS_ADMIN : TABS_USER;

  const dateKey = (isoString) => {
    if (!isoString) return '';
    const s = String(isoString);
    return s.length >= 10 ? s.slice(0, 10) : '';
  };

  useEffect(() => {
    const fromUrl = searchParams.get('tab');
    if (!fromUrl) return;
    const allowed = new Set(isAdmin ? ['tous', 'en_cours', 'livres', 'echecs'] : ['depot', 'tous', 'en_cours', 'livres', 'echecs']);
    if (allowed.has(fromUrl)) setTab(fromUrl);
  }, [isAdmin, searchParams]);

  // Filtrage selon l'onglet et la zone
  const filtered = list.filter(c => {
    const dateOk = !filterDate || dateKey(c.date) === filterDate;
    if (!dateOk) return false;
    const zoneOk = !filterZone || c.zone === filterZone;
    if (!zoneOk) return false;
    if (tab === 'tous')     return true;
    if (tab === 'en_cours') return ['affecte', 'en_route'].includes(c.statut);
    if (tab === 'livres')   return c.statut === 'livre';
    if (tab === 'echecs')   return c.statut === 'echec';
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 20px' }}>

        {/* ── Stats ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 28 }}>
          <StatCard icon="📦" label="Total colis"  value={effectiveStats.total}              color="#1e293b" bg="#f1f5f9" />
          <StatCard icon="⏳" label="En attente"   value={effectiveStats.depot}              color="#D97706" bg="#FEF3C7" />
          <StatCard icon="🚚" label="En cours"     value={effectiveStats.en_cours}           color="#0284C7" bg="#E0F2FE" />
          <StatCard icon="✅" label="Livrés"       value={effectiveStats.livre}              color="#059669" bg="#D1FAE5" />
          <StatCard icon="💰" label="CA livraisons" value={formatFCFA(effectiveStats.ca)}    color="#7C3AED" bg="#EDE9FE" />
        </div>

        {/* ── Tabs ── */}
        <div style={{
          display:     'flex',
          gap:         6,
          marginBottom: 20,
          background:  'white',
          padding:     6,
          borderRadius: 16,
          boxShadow:   '0 2px 8px rgba(0,0,0,0.06)',
          width:       'fit-content',
          flexWrap:    'wrap',
        }}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                if (t.key === 'depot') setFilterZone('');
              }}
              style={{
              padding:      '10px 18px',
              borderRadius: 12,
              border:       'none',
              cursor:       'pointer',
              fontWeight:   700,
              fontSize:     13,
              fontFamily:   'inherit',
              display:      'flex',
              alignItems:   'center',
              gap:          7,
              background:   tab === t.key ? 'linear-gradient(135deg, #4F46E5, #8B5CF6)' : 'transparent',
              color:        tab === t.key ? 'white' : '#64748b',
              boxShadow:    tab === t.key ? '0 4px 12px rgba(79,70,229,0.3)' : 'none',
              transition:   'all 0.2s',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── Filtres ── */}
        {tab !== 'depot' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Date :</span>
            <input
              type="date"
              value={filterDate}
              onChange={e => setFilterDate(e.target.value)}
              style={{
                padding:      '8px 14px',
                borderRadius: 10,
                border:       '2px solid #e2e8f0',
                fontSize:     13,
                fontFamily:   'inherit',
                background:   'white',
                color:        '#1e293b',
                outline:      'none',
                cursor:       'pointer',
              }}
            />
            {filterDate && (
              <button type="button" onClick={() => setFilterDate('')} style={{
                padding:      '8px 12px',
                borderRadius: 10,
                border:       'none',
                fontSize:     13,
                fontFamily:   'inherit',
                background:   '#F1F5F9',
                color:        '#475569',
                cursor:       'pointer',
                fontWeight:   800,
              }}>
                Effacer
              </button>
            )}

            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Zone :</span>
            <select
              value={filterZone}
              onChange={e => setFilterZone(e.target.value)}
              style={{
                padding:      '8px 14px',
                borderRadius: 10,
                border:       '2px solid #e2e8f0',
                fontSize:     13,
                fontFamily:   'inherit',
                background:   'white',
                color:        '#1e293b',
                outline:      'none',
                cursor:       'pointer',
              }}
            >
              <option value="">Toutes les zones</option>
              {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
            {filterZone && (
              <button onClick={() => setFilterZone('')} style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                ✕ Effacer
              </button>
            )}
          </div>
        )}

        {/* ── Contenu ── */}
        {!isAdmin && tab === 'depot' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,500px) 1fr', gap: 28, alignItems: 'start' }}>
            {/* Formulaire */}
            <div style={{ background: 'white', borderRadius: 20, padding: 28, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', marginBottom: 24 }}>
                📥 Nouveau dépôt
              </div>
              <FormDepot onSuccess={() => setTab('tous')} />
            </div>

            {/* Derniers en attente */}
            <div>
              <SectionTitle count={list.filter(c => c.statut === 'depot').length}>
                En attente de réception
              </SectionTitle>
              <ColisGrid colis={list.filter(c => c.statut === 'depot')} onAffecter={setModalId} />
            </div>
          </div>
        ) : (
          <>
            <SectionTitle count={filtered.length}>
              {tabs.find(t => t.key === tab)?.label}
              {filterZone && ` · ${filterZone}`}
            </SectionTitle>
            <ColisGrid colis={filtered} onAffecter={setModalId} />
          </>
        )}
      </main>

      {/* Modal + Toast */}
      <ModalAffecter colisId={modalId} onClose={() => setModalId(null)} />
      <Toast />
    </div>
  );
}

// ─── Sous-composants ────────────────────────────────────────────────────────

function SectionTitle({ count, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <span style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>{children}</span>
      <span style={{
        background: '#EEF2FF', color: '#4F46E5',
        borderRadius: 20, padding: '2px 10px',
        fontSize: 12, fontWeight: 700,
      }}>
        {count}
      </span>
    </div>
  );
}

function ColisGrid({ colis, onAffecter }) {
  if (colis.length === 0) {
    return (
      <div style={{
        background:   'white',
        borderRadius: 16,
        padding:      60,
        textAlign:    'center',
        color:        '#94a3b8',
        boxShadow:    '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Aucun colis ici</div>
        <div style={{ fontSize: 14 }}>Les colis apparaîtront selon leur statut</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
      {colis.map(c => (
        <ColisCard key={c.id} colis={c} onAffecter={onAffecter} />
      ))}
    </div>
  );
}
